/**
 * CommentLikeButton
 *
 * Heart-with-bubbles toggle for a comment, reused from ContentActions'
 * AnimatedBubblesButton but sized down for inline comment rows.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AnimatedBubblesButton } from '@/presentation/feature/content/components/AnimatedBubblesButton';
import { useCommentLike } from '@/presentation/feature/content/hooks/useCommentLike';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface CommentLikeButtonProps {
  commentId: string;
  likes: number;
  size: number;
  onToggle: (liked: boolean) => void;
}

export const CommentLikeButton: React.FC<CommentLikeButtonProps> = ({
  commentId,
  likes,
  size,
  onToggle,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { isLiked } = useCommentLike(commentId);

  return (
    <div
      {...qaAttr({
        id: `comment-like-${commentId}`,
        status: isLiked ? 'liked' : 'unliked',
      })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: responsive.gap.xs,
      }}
    >
      <AnimatedBubblesButton
        contentId={commentId}
        isLiked={isLiked}
        size={size}
        likeColor={theme.activeRed}
        unlikeColor={theme.icon.primary}
        onTap={() => onToggle(!isLiked)}
        animationDuration={ANIMATION_CONSTANTS.duration.long}
        circleColor={theme.exclusive}
        iconUrl="/assets/images/heart_fill.png"
        bubbles={[
          { color: theme.activeRed },
          { color: theme.blue },
          { color: theme.exclusive },
          { color: theme.exclusive },
          { color: theme.blue },
          { color: theme.exclusive },
        ]}
      />
      <span style={{ fontSize: responsive.font.xs, color: theme.text.secondary }}>
        {MathUtils.formatCount(likes)}
      </span>
    </div>
  );
};
